import React, { useState, useEffect, useCallback } from 'react';
import { Plus, FileText, Edit2, Trash2, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { templateService } from '../services/templateService';
import CreateTemplateModal from './CreateTemplateModal';
import type { QuestionnaireTemplate } from '../types/questionnaires';

const TemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<QuestionnaireTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await templateService.listTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleDelete = async (template: QuestionnaireTemplate) => {
    if (template.isSystem) {
      alert('System-Vorlagen können nicht gelöscht werden.');
      return;
    }

    const confirmed = window.confirm(
      `Möchten Sie die Vorlage "${template.name}" wirklich löschen?\n\n` +
      `Diese Aktion kann nicht rückgängig gemacht werden.`
    );

    if (!confirmed) return;

    try {
      await templateService.deleteTemplate(template.id);
      alert('Vorlage erfolgreich gelöscht');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Fehler beim Löschen der Vorlage');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Fragebogen-Vorlagen
          </h1>
          <p className="text-gray-600">
            Verwalten Sie Vorlagen für verschiedene Fragebogen-Typen
          </p>
        </div>
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowCreateModal(true);
          }}
          className="bg-purple-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-purple-700 transition-colors font-semibold shadow-md"
        >
          <Plus className="w-5 h-5" />
          Neue Vorlage erstellen
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Noch keine Vorlagen
          </h2>
          <p className="text-gray-600 mb-6">
            Erstellen Sie Ihre erste Vorlage, um Fragebögen zu definieren.
          </p>
        </div>
      ) : (
        <div className="grid gap-6">
          {templates.map(template => (
            <div
              key={template.id}
              className="bg-white rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <FileText className="w-6 h-6 text-purple-600 mr-3" />
                      <h3 className="text-xl font-bold text-gray-900">{template.name}</h3>
                      {template.isSystem && (
                        <span className="ml-3 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          System
                        </span>
                      )}
                    </div>
                    
                    {template.description && (
                      <p className="text-gray-600 mb-4">{template.description}</p>
                    )}
                    
                    <div className="text-sm text-gray-500 mb-3">
                      {template.fields.length} Feld{template.fields.length !== 1 ? 'er' : ''}
                      {' • '}
                      Erstellt: {new Date(template.createdAt).toLocaleDateString('de-DE')}
                    </div>

                    {/* Field Preview */}
                    <button
                      onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}
                      className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                    >
                      {expandedTemplate === template.id ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Weniger anzeigen
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Felder anzeigen
                        </>
                      )}
                    </button>

                    {expandedTemplate === template.id && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Felder:</h4>
                        <div className="space-y-2">
                          {template.fields
                            .sort((a, b) => a.order - b.order)
                            .map((field, idx) => (
                              <div key={field.id} className="flex items-center gap-3 text-sm bg-gray-50 p-2 rounded">
                                <span className="text-gray-500 font-mono">{idx + 1}.</span>
                                <span className="font-medium">{field.label}</span>
                                <span className="text-gray-500">
                                  ({field.type === 'text' ? 'Text' : 'Textarea'})
                                </span>
                                {field.required && (
                                  <span className="text-red-600 text-xs font-medium">Pflichtfeld</span>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4 flex flex-col gap-2">
                    <button
                      onClick={() => {
                        setEditingTemplate(template);
                        setShowCreateModal(true);
                      }}
                      className="px-4 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Bearbeiten
                    </button>
                    
                    {!template.isSystem && (
                      <button
                        onClick={() => handleDelete(template)}
                        className="px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Löschen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {showCreateModal && (
        <CreateTemplateModal 
          template={editingTemplate}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTemplate(null);
          }}
          onSaved={loadTemplates}
        />
      )}
    </div>
  );
};

export default TemplateManagement;

